import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import {
  ArrowLeft, Loader2, User, Instagram, Hash, MapPin, Ruler, CalendarDays,
  Trophy, Sparkles, ImageIcon, ZoomIn,
} from "lucide-react";
import { Lightbox } from "@/components/Lightbox";
import { Footer } from "@/components/landing/FooterCTA";
import { playerAvatar } from "@/components/plantel/PlayerCard";
import { Navbar } from "@/components/landing/Navbar";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import logo from "../assets/logo.png";

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
function fmtBirth(d) {
  if (!d) return null;
  const [y, m, day] = d.slice(0, 10).split("-");
  if (!y) return null;
  const base = day && m ? `${parseInt(day, 10)} de ${MONTHS[parseInt(m, 10) - 1]} de ${y}` : y;
  const age = new Date().getFullYear() - parseInt(y, 10);
  return `${base} (${age} años)`;
}

function InfoChip({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
      <div className="h-9 w-9 rounded-lg bg-gold/15 text-gold flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">{label}</div>
        <div className="text-sm font-semibold text-white truncate">{value}</div>
      </div>
    </div>
  );
}

function Block({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-7">
      <div className="flex items-center gap-2 text-navy font-bold">
        <Icon className="h-5 w-5 text-gold" /> {title}
      </div>
      <div className="mt-3 text-gray-600 leading-relaxed whitespace-pre-line">{children}</div>
    </div>
  );
}

export default function PlayerPage() {
  const { slug } = useParams();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/plantel/players/by-slug/${slug}`)
      .then(({ data }) => setPlayer(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const gallery = player
    ? [player.photo_url, ...(player.photos || [])].filter(Boolean).map((url, i) => ({ url, alt: `${player.name} - foto ${i + 1}` }))
    : [];
  const nav = useCallback((dir) => {
    setLightbox((i) => (i === null ? i : (i + dir + gallery.length) % gallery.length));
  }, [gallery.length]);

  return (
    <div className="min-h-screen bg-[#eef1f7] flex flex-col">
      <Navbar />
      <div className="pt-16 sm:pt-20">
        <Breadcrumbs items={[
          { label: "Plantel", to: "/plantel" },
          ...(player?.roster ? [{ label: player.roster.category, to: `/plantel/${player.roster.slug}` }] : []),
          { label: player ? player.name : "Jugador" },
        ]} />
      </div>

      {loading ? (
        <div className="flex-1 py-24 flex justify-center text-navy/40"><Loader2 className="h-7 w-7 animate-spin" /></div>
      ) : notFound || !player ? (
        <div className="flex-1 py-24 text-center text-gray-400">
          <User className="h-10 w-10 mx-auto" />
          <p className="mt-3">No encontramos este jugador.</p>
          <Link to="/plantel" className="mt-4 inline-block text-sm font-semibold text-navy underline">Ver planteles</Link>
        </div>
      ) : (
        <>
          {/* Hero */}
          <section className="relative bg-navy text-white overflow-hidden">
            <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-gold/15 blur-3xl" />
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 grid lg:grid-cols-5 gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
                className="lg:col-span-2"
              >
                <div className="relative aspect-[3/4] max-w-xs mx-auto lg:mx-0 rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-[#16235a]">
                  <img
                    src={player.photo_url || playerAvatar(player.name)}
                    alt={player.name} data-testid="player-photo"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = playerAvatar(player.name); }}
                    className="w-full h-full object-cover"
                  />
                  {player.number && (
                    <span className="absolute top-4 right-4 h-12 w-12 rounded-full bg-gold text-navy text-lg font-extrabold flex items-center justify-center shadow-lg" style={{ fontFamily: "Outfit" }}>
                      {player.number}
                    </span>
                  )}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
                className="lg:col-span-3"
              >
                {player.roster && (
                  <Link to={`/plantel/${player.roster.slug}`} className="inline-block section-tag section-tag--dark hover:opacity-80">
                    {player.roster.category}{player.roster.subcategory ? ` · ${player.roster.subcategory}` : ""} · {player.roster.season}
                  </Link>
                )}
                <h1 data-testid="player-name" className="mt-4 text-4xl sm:text-6xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>
                  {player.name}
                </h1>
                {player.position && <p className="mt-2 text-lg font-semibold text-gold">{player.position}</p>}

                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  <InfoChip icon={Hash} label="Número" value={player.number} />
                  <InfoChip icon={CalendarDays} label="Nacimiento" value={fmtBirth(player.birthdate)} />
                  <InfoChip icon={Ruler} label="Altura" value={player.height} />
                  <InfoChip icon={MapPin} label="Nacionalidad" value={player.nationality} />
                  {player.instagram && (
                    <a
                      href={`https://instagram.com/${player.instagram.replace("@", "")}`}
                      target="_blank" rel="noreferrer" data-testid="player-instagram"
                      className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:border-gold/50 transition-colors"
                    >
                      <div className="h-9 w-9 rounded-lg bg-gold/15 text-gold flex items-center justify-center shrink-0"><Instagram className="h-4 w-4" /></div>
                      <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">Instagram</div>
                        <div className="text-sm font-semibold text-white truncate">{player.instagram}</div>
                      </div>
                    </a>
                  )}
                </div>
              </motion.div>
            </div>
          </section>

          {/* Body */}
          <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-10 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {player.bio && <Block icon={User} title="Biografía">{player.bio}</Block>}
              {player.trajectory && <Block icon={Trophy} title="Trayectoria">{player.trajectory}</Block>}
              {player.other && <Block icon={Sparkles} title="Otros datos">{player.other}</Block>}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-2 text-navy font-bold">
                  <ImageIcon className="h-5 w-5 text-gold" /> Galería
                </div>
                {gallery.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-400">Sin fotos disponibles.</p>
                ) : (
                  <div className="mt-4 grid grid-cols-3 gap-2.5">
                    {gallery.map((g, i) => (
                      <button
                        key={i}
                        data-testid={`player-gallery-${i}`}
                        onClick={() => setLightbox(i)}
                        className="group relative aspect-square rounded-lg overflow-hidden border border-gray-100 cursor-zoom-in"
                      >
                        <img src={g.url} alt={g.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <span className="absolute inset-0 bg-navy/0 group-hover:bg-navy/25 transition-colors flex items-center justify-center">
                          <ZoomIn className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </main>
        </>
      )}

      <AnimatePresence>
        {lightbox !== null && gallery.length > 0 && (
          <Lightbox items={gallery} index={lightbox} onClose={() => setLightbox(null)} onNav={nav} />
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
